import React, { useEffect } from 'react';

/**
 * Legal & Support Information Modal for UTMS
 * Supports: 'terms' | 'privacy' | 'contact'
 */
function LegalModal({ type, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!type) return null;

  const contentMap = {
    terms: {
      badge: 'Legal & Compliance',
      title: 'Terms & Conditions of Operation',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
      sections: [
        {
          heading: '1. Authorized Airspace Operations',
          body: 'Access to the BERAM Unmanned Traffic Management System (UTMS) is restricted to certified Remote Pilots, Fleet Operators, and Airspace Authorities. All flight operations conducted through this console must comply with Civil Aviation Regulations (CAR Section 3, Series X) and national airspace guidelines.'
        },
        {
          heading: '2. Pilot-in-Command (PIC) Responsibility',
          body: 'The human Pilot-in-Command retains final legal authority and responsibility for the safe operation of each unmanned aircraft. Automated geofencing, deconfliction advisories, and path generation provided by UTMS are situational awareness aids and do not absolve the operator of line-of-sight and airspace vigilance.'
        },
        {
          heading: '3. Real-Time Telemetry & Fail-Safe Auditing',
          body: 'By connecting to this ground control station, you consent to continuous telemetry recording (GPS coordinates, altitude, groundspeed, battery levels, and control link quality) for airspace safety auditing, collision prevention, and incident forensics.'
        },
        {
          heading: '4. Restricted Zones & Emergency Takeover',
          body: 'Flight plans passing through active No-Fly Zones (NFZs) or dynamic temporary flight restrictions (TFRs) will be automatically rejected. In case of airspace emergency or low-battery threshold breaches, the system reserves the right to trigger Return-to-Home (RTL) or emergency hover commands.'
        }
      ]
    },
    privacy: {
      badge: 'Data Security & Privacy',
      title: 'UTMS Telemetry & Data Privacy Policy',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      sections: [
        {
          heading: '1. What Telemetry We Collect',
          body: 'When your fleet is armed or active, the UTMS infrastructure collects MAVLink telemetry packets including vehicle position, attitude (pitch, roll, yaw), sensor health, battery state, and command acknowledgment logs.'
        },
        {
          heading: '2. Purpose of Processing',
          body: 'Telemetry data is processed strictly for: (a) real-time 3D airspace deconfliction and collision avoidance, (b) automatic logbook generation, and (c) flight playback and compliance reporting.'
        },
        {
          heading: '3. Data Encryption & Transmission',
          body: 'All communications between drones, ground stations, and the UTMS backend are transmitted over TLS-encrypted WebSockets and secure HTTPS APIs. Stored flight snapshots are cryptographically signed.'
        },
        {
          heading: '4. Video & Payload Streams',
          body: 'Live camera video feeds are transmitted point-to-point via secure WebRTC protocols. Payload streams are never stored on public servers without explicit enterprise operator authorization.'
        }
      ]
    },
    contact: {
      badge: 'Support & Mission Control',
      title: 'Contact UTMS Operations Desk',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ),
      sections: [
        {
          heading: 'Support',
          body: 'For account, mission or telemetry questions, email us. This inbox is not watched around the clock: in an emergency, follow your local emergency and aviation procedures.'
        },
        {
          heading: 'Direct Channels',
          customHtml: true,
          content: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', padding: '10px 14px', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)' }}>
                <span style={{ fontSize: '18px' }}>✉️</span>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--textMuted)' }}>Operations & Fleet Email</div>
                  <a href="mailto:contactberam@gmail.com" style={{ color: 'var(--accent, var(--accent))', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>
                    contactberam@gmail.com
                  </a>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'color-mix(in srgb, var(--textPrimary) 3%, transparent)', padding: '10px 14px', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--textPrimary) 6%, transparent)' }}>
                <span style={{ fontSize: '18px' }}>🏢</span>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--textMuted)' }}>BERAM Robotics Technologies</div>
                  <div style={{ color: 'var(--textPrimary, #fff)', fontSize: '12px' }}>
                    Unmanned Traffic Management Division, New Delhi, India
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    }
  };

  const modalData = contentMap[type] || contentMap.terms;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'rgba(0, 0, 0, 0.78)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        animation: 'legalFadeIn 0.18s ease-out',
      }}
    >
      <style>{`
        @keyframes legalFadeIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        .legal-modal-body::-webkit-scrollbar {
          width: 6px;
        }
        .legal-modal-body::-webkit-scrollbar-track {
          background: color-mix(in srgb, var(--textPrimary) 2%, transparent);
        }
        .legal-modal-body::-webkit-scrollbar-thumb {
          background: color-mix(in srgb, var(--accent) 25%, transparent);
          border-radius: 3px;
        }
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '85vh',
          background: 'rgba(18, 20, 24, 0.96)',
          border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
          borderRadius: '14px',
          boxShadow: '0 24px 60px -10px rgba(0, 0, 0, 0.85), 0 0 25px -5px color-mix(in srgb, var(--accent) 20%, transparent)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: "'Inter', 'Segoe UI', sans-serif",
          color: 'var(--textPrimary)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px',
          borderBottom: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
          background: 'rgba(25, 28, 33, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: 'color-mix(in srgb, var(--accent) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
              color: 'var(--accent, var(--accent))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              {modalData.icon}
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent, var(--accent))', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {modalData.badge}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#fff', marginTop: '1px' }}>
                {modalData.title}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              border: '1px solid color-mix(in srgb, var(--textPrimary) 10%, transparent)',
              background: 'color-mix(in srgb, var(--textPrimary) 4%, transparent)',
              color: 'var(--textSecondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '15px',
              transition: 'all 0.15s ease',
              padding: 0,
              margin: 0,
            }}
            title="Close (Esc)"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Body */}
        <div
          className="legal-modal-body"
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {modalData.sections.map((sec, idx) => (
            <div key={idx} style={{ background: 'color-mix(in srgb, var(--textPrimary) 2%, transparent)', padding: '12px 14px', borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--textPrimary) 4%, transparent)' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
                {sec.heading}
              </div>
              {sec.customHtml ? (
                sec.content
              ) : (
                <div style={{ fontSize: '12.5px', color: '#94a3b8', lineHeight: 1.6 }}>
                  {sec.body}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid color-mix(in srgb, var(--textPrimary) 8%, transparent)',
          background: 'rgba(25, 28, 33, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '6px',
              background: 'var(--accent, var(--accent))',
              color: '#00363d',
              border: 'none',
              fontWeight: 700,
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              margin: 0,
              width: 'auto',
            }}
          >
            Acknowledge & Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default LegalModal;
